import { useMemo, useState } from "react";
import {
  Filter,
  ListTree,
  MousePointerClick,
  Search,
  Settings2,
  X,
} from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateDepartmentDialog } from "@/features/departments/components/CreateDepartmentDialog";
import { DepartmentTree } from "@/features/departments/components/DepartmentTree";
import { DepartmentDetailPanel } from "@/features/departments/components/DepartmentDetailPanel";
import { DepartmentsStats } from "@/features/departments/components/DepartmentsStats";
import { useDepartmentTree } from "@/features/departments/hooks/useDepartmentsQueries";
import {
  computeDepartmentStats,
  filterDepartmentTree,
  hasActiveDepartmentFilters,
  type DepartmentTreeFilters,
} from "@/features/departments/utils/department-tree";
import { useDepartmentPermissions } from "@/hooks/useDepartmentPermissions";
import type { DepartmentTreeNode } from "@/types/department";

export default function DepartmentsPage() {
  const { data: tree = [], isLoading, error } = useDepartmentTree();
  const { canManage } = useDepartmentPermissions();
  const [selectedDepartment, setSelectedDepartment] =
    useState<DepartmentTreeNode | null>(null);
  const [search, setSearch] = useState("");
  const [requiresApproval, setRequiresApproval] = useState<boolean | undefined>(
    undefined,
  );
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters: DepartmentTreeFilters = {
    search,
    requiresApproval,
    isActive,
  };

  const filteredTree = useMemo(
    () => filterDepartmentTree(tree, filters),
    [tree, filters],
  );

  const stats = useMemo(
    () => computeDepartmentStats(filteredTree),
    [filteredTree],
  );

  const activeFilters = hasActiveDepartmentFilters(filters);

  const clearFilters = () => {
    setSearch("");
    setRequiresApproval(undefined);
    setIsActive(undefined);
  };

  return (
    <AdminPageShell
      title="الهيكل التنظيمي"
      description="إدارة الهيكل التنظيمي للوزارة وإعدادات الهيكل التنظيمي"
      isLoading={isLoading}
      error={error}
      actions={canManage ? <CreateDepartmentDialog /> : undefined}
    >
      <DepartmentsStats stats={stats} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 min-h-150">
        <Card>
          <CardContent className="space-y-4 p-4">
            <SectionHeader title="قائمة الهيكل التنظيمي" icon={ListTree} />

            <div className="space-y-4 rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="size-5 text-muted-foreground" />
                  <h3 className="font-semibold text-foreground">
                    فلتر الدوائر
                  </h3>
                </div>
                {activeFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="border-destructive/20 text-destructive hover:bg-destructive/5"
                  >
                    <X className="mr-1 size-4" />
                    مسح الفلاتر
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="relative md:col-span-1">
                  <Search className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="ابحث بالاسم أو الكود..."
                    className="pr-10"
                  />
                </div>

                <Select
                  value={
                    requiresApproval === undefined
                      ? "ALL"
                      : String(requiresApproval)
                  }
                  onValueChange={(v) =>
                    setRequiresApproval(v === "ALL" ? undefined : v === "true")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="سير عمل الموافقة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">كل أنواع النشر</SelectItem>
                    <SelectItem value="true">يتطلب موافقة</SelectItem>
                    <SelectItem value="false">نشر مباشر</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={isActive === undefined ? "ALL" : String(isActive)}
                  onValueChange={(v) =>
                    setIsActive(v === "ALL" ? undefined : v === "true")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">كل الحالات</SelectItem>
                    <SelectItem value="true">نشط</SelectItem>
                    <SelectItem value="false">غير نشط</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFilters && (
                <div className="flex flex-wrap gap-2 border-t pt-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    الفلاتر النشطة:
                  </span>
                  {search && <Badge variant="secondary">البحث: {search}</Badge>}
                  {requiresApproval !== undefined && (
                    <Badge variant="secondary">
                      الموافقة: {requiresApproval ? "نعم" : "لا"}
                    </Badge>
                  )}
                  {isActive !== undefined && (
                    <Badge variant="secondary">
                      الحالة: {isActive ? "نشط" : "غير نشط"}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <DepartmentTree
              nodes={filteredTree}
              selectedId={selectedDepartment?.id}
              onSelect={setSelectedDepartment}
              forceExpanded={activeFilters}
              hasActiveFilters={activeFilters}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-4">
            <SectionHeader title="إعدادات الدائرة" icon={Settings2} />
            {selectedDepartment ? (
              <DepartmentDetailPanel departmentId={selectedDepartment.id} />
            ) : (
              <EmptyState
                icon={MousePointerClick}
                title="اختر دائرة"
                description="اختر دائرة من الشجرة لعرض إعداداتها وتعيين المدير وملكية المحتوى."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageShell>
  );
}
